import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Loads Google Maps JS API (Places) once per page
let googleMapsLoader;
function loadGoogleMaps() {
  if (typeof window !== "undefined" && window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve();
  }
  if (googleMapsLoader) return googleMapsLoader;
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  googleMapsLoader = new Promise((resolve, reject) => {
    if (!key) {
      console.warn("REACT_APP_GOOGLE_MAPS_API_KEY not set; AddressAutocomplete will behave as a plain input.");
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&v=weekly`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = (e) => {
      console.error("Failed to load Google Maps JS API", e);
      reject(e);
    };
    document.head.appendChild(script);
  });
  return googleMapsLoader;
}

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Enter address",
  className = "",
  inputProps = {},
  country = "au",
  melbourneBias = true,
  strictBounds = false,
  bounds,
  showClear = true,
  minLength = 1,
  debounceMs = 180,
}) {
  const inputRef = useRef(null);
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const debTimer = useRef(null);
  const servicesRef = useRef({ ac: null, places: null, biasBounds: null });
  const suppressNextFocusRef = useRef(false);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 224 });
  const [usePortal, setUsePortal] = useState(true);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then(() => {
        if (!mounted || !window.google) return;
        const gmaps = window.google.maps;
        const ac = new gmaps.places.AutocompleteService();
        const places = new gmaps.places.PlacesService(document.createElement('div'));
        let useBounds = bounds || null;
        if (!useBounds && melbourneBias) {
          try {
            const sw = new gmaps.LatLng(-38.5, 144.4);
            const ne = new gmaps.LatLng(-37.5, 145.6);
            useBounds = new gmaps.LatLngBounds(sw, ne);
          } catch {}
        }
        servicesRef.current = { ac, places, biasBounds: useBounds };
      })
      .catch(() => {});
    return () => {
      mounted = false;
      if (debTimer.current) clearTimeout(debTimer.current);
    };
  }, [bounds, melbourneBias]);

  const hasValue = (value ?? "").length > 0;

  // Decide portal usage based on viewport (mobile -> portal; desktop -> inline)
  useEffect(() => {
    const update = () => {
      try {
        setUsePortal((window.innerWidth || 0) < 768); // Tailwind md breakpoint
      } catch {
        setUsePortal(true);
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  const requestPredictions = (q) => {
    const { ac, biasBounds } = servicesRef.current || {};
    if (!ac || !q || q.length < minLength) {
      setPredictions([]);
      setOpen(false);
      return;
    }
    const opts = { input: q, types: ['address'] };
    if (biasBounds) {
      opts.bounds = biasBounds;
      if (strictBounds) opts.strictBounds = true;
    }
    if (country) opts.componentRestrictions = { country };
    try {
      ac.getPlacePredictions(opts, (res = [], status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK && status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setPredictions([]);
          setOpen(false);
          return;
        }
        setPredictions(res || []);
        setOpen((res || []).length > 0);
      });
    } catch {
      setPredictions([]);
      setOpen(false);
    }
  };

  const handleChange = (e) => {
    onChange?.(e);
    const q = e.target.value;
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(() => requestPredictions(q), debounceMs);
  };

  const handleFocus = () => {
    if (suppressNextFocusRef.current) {
      suppressNextFocusRef.current = false;
      return;
    }
    const q = (value || '').trim();
    if (q.length >= minLength) requestPredictions(q);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const first = predictions[0];
      if (first) {
        e.preventDefault();
        handlePick(first);
      }
    }
  };

  const handlePick = (prediction) => {
    const { places } = servicesRef.current || {};
    if (!places || !prediction?.place_id) {
      const text = prediction?.description || '';
      onChange?.({ target: { value: text } });
      onSelect?.({ description: text, placeId: null, lat: null, lng: null, addressComponents: [] });
      setOpen(false);
      return;
    }
    const req = { placeId: prediction.place_id, fields: ['formatted_address', 'address_components', 'geometry'] };
    places.getDetails(req, (place, status) => {
      const ok = status === window.google.maps.places.PlacesServiceStatus.OK && place;
      const formatted = ok ? (place.formatted_address || prediction.description) : prediction.description;
      const lat = ok && place.geometry?.location?.lat ? place.geometry.location.lat() : null;
      const lng = ok && place.geometry?.location?.lng ? place.geometry.location.lng() : null;
      onChange?.({ target: { value: formatted } });
      onSelect?.({ description: formatted, placeId: prediction.place_id, lat, lng, addressComponents: ok ? (place.address_components || []) : [] });
      setOpen(false);
      setPredictions([]);
      suppressNextFocusRef.current = true;
      try { inputRef.current?.focus(); } catch {}
    });
  };

  useEffect(() => {
    let raf = null;
    const scheduleUpdate = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const top = r.bottom;
        // Prevent overlap with fixed bottom nav (mobile) by reserving space
        const NAV_SAFE = 80; // px
        const vh = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0);
        const avail = Math.max(120, vh - top - NAV_SAFE);
        setDropdownPos({ top, left: r.left, width: r.width, maxHeight: avail });
      });
    };

    if (open && usePortal) {
      scheduleUpdate();
      window.addEventListener('scroll', scheduleUpdate, true);
      window.addEventListener('resize', scheduleUpdate);
      window.addEventListener('orientationchange', scheduleUpdate);
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (usePortal) {
        window.removeEventListener('scroll', scheduleUpdate, true);
        window.removeEventListener('resize', scheduleUpdate);
        window.removeEventListener('orientationchange', scheduleUpdate);
      }
    };
  }, [open, value, usePortal]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const dropdownEl = dropdownRef.current;
      const inputEl = inputRef.current;
      if (!dropdownEl || !inputEl) return;
      if (dropdownEl.contains(e.target) || inputEl.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full h-9 rounded-lg border px-3 pr-9 control-text outline-none focus:ring-2 focus:ring-primary"
        autoComplete="off"
        {...inputProps}
      />
      {showClear && hasValue && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onChange?.({ target: { value: "" } });
            setPredictions([]);
            setOpen(false);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          aria-label="Clear"
          title="Clear"
          className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-gray-500 hover:text-gray-700 flex items-center justify-center"
        >
          <span aria-hidden>×</span>
        </button>
      )}
      {open && predictions.length > 0 && (
        usePortal
          ? createPortal(
              <ul
                ref={dropdownRef}
                className="bg-white border rounded-lg shadow-lg overflow-auto"
                style={{ position: 'fixed', top: dropdownPos.top + 4, left: dropdownPos.left, width: dropdownPos.width, zIndex: 35, maxHeight: dropdownPos.maxHeight }}
              >
                {predictions.map((p) => (
                  <li key={p.place_id}>
                    <button
                      type="button"
                      onClick={() => handlePick(p)}
                      className="w-full text-left px-3 py-2 control-text hover:bg-red-50"
                      title={p.description}
                    >
                      {p.description}
                    </button>
                  </li>
                ))}
              </ul>,
              document.body
            )
          : (
              <ul
                ref={dropdownRef}
                className="absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg overflow-auto z-30"
                style={{ maxHeight: 224 }}
              >
                {predictions.map((p) => (
                  <li key={p.place_id}>
                    <button
                      type="button"
                      onClick={() => handlePick(p)}
                      className="w-full text-left px-3 py-2 control-text hover:bg-red-50"
                      title={p.description}
                    >
                      {p.description}
                    </button>
                  </li>
                ))}
              </ul>
            )
      )}
    </div>
  );
}
